import React, { useState, useEffect } from 'react';
import { Table, Button, Badge, Card, Row, Col, Form, InputGroup, Alert } from 'react-bootstrap';
import { Search, Edit, Trash, PlusCircle, RefreshCw } from 'lucide-react';
import { getAllUsers, deleteUser } from '../../api/users';
import useAuth from '../../hooks/useAuth';

const UserList = ({ onAddUser, onEditUser }) => {
    const { isAdmin } = useAuth();
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                setLoading(true);
                setError('');
                const data = await getAllUsers();
                setUsers(data);
                setFilteredUsers(data);
            } catch (err) {
                setError('Impossible de charger les utilisateur. Veuillez réessayer.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, [refreshTrigger]);

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredUsers(users);
        } else {
            const filtered = users.filter(user =>
                user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredUsers(filtered);
        }
    }, [searchTerm, users]);

    const handleRefresh = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            try {
                await deleteUser(id);
                setUsers(users.filter(user => user.id !== id));
                setFilteredUsers(filteredUsers.filter(user => user.id !== id));
            } catch (err) {
                setError('Failed to delete user. Please try again.');
                console.error(err);
            }
        }
    };

    const getRoleBadge = (role) => {
        const roleType = role.name; // Remove 'ROLE_' prefix

        switch (roleType) {
            case 'ADMIN':
                return <Badge bg="danger">Admin</Badge>;
            case 'MANAGER':
                return <Badge bg="primary">Manager</Badge>;
            case 'SALES_REP':
                return <Badge bg="info">Commercial</Badge>;
            case 'DISTRIBUTOR':
                return <Badge bg="success">Distributeur</Badge>;
            case 'WAREHOUSE_MANAGER':
                return <Badge bg="warning">Manager entrepot</Badge>;
            default:
                return <Badge bg="secondary">{roleType}</Badge>;
        }
    };

    return (
        <Card className="shadow-sm">
            <Card.Header>
                <Row className="align-items-center">
                    <Col>
                    </Col>
                    <Col xs="auto">
                        <Button
                            variant="success"
                            size="sm"
                            className="me-2"
                            onClick={onAddUser}
                            disabled={!isAdmin}
                        >
                            <PlusCircle size={16} className="me-1" />
                            Ajouter Utilisateur
                        </Button>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleRefresh}
                        >
                            <RefreshCw size={16} />
                        </Button>
                    </Col>
                </Row>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}

                <InputGroup className="mb-3">
                    <InputGroup.Text>
                        <Search size={18} />
                    </InputGroup.Text>
                    <Form.Control
                        placeholder="Rechercher utilisateur par nom, identifiant ou adresse e-mail..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </InputGroup>

                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2">Chargement utilisateurs...</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <Table striped hover>
                            <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Nom d'utilisateur</th>
                                    <th>Email</th>
                                    <th>Roles</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.length > 0 ? (
                                    filteredUsers.map(user => (
                                        <tr key={user.id}>
                                            <td>{user.fullName}</td>
                                            <td>{user.username}</td>
                                            <td>{user.email}</td>
                                            <td>
                                                {user.roles.map((role, index) => (
                                                    <span key={index} className="me-1">
                                                        {getRoleBadge(role)}
                                                    </span>
                                                ))}
                                            </td>
                                            <td>
                                                {user.active ? (
                                                    <Badge bg="success">Actif</Badge>
                                                ) : (
                                                    <Badge bg="danger">Inactif</Badge>
                                                )}
                                                {user.firstLogin && (
                                                    <Badge bg="warning" className="ms-1">P Login</Badge>
                                                )}
                                            </td>
                                            <td>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="me-1"
                                                    onClick={() => onEditUser(user.id)}
                                                >
                                                    <Edit size={16} />
                                                </Button>
                                                {isAdmin && (
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => handleDelete(user.id)}
                                                    >
                                                        <Trash size={16} />
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center py-3">
                                            No users found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

export default UserList;